import { Toaster } from '@/components/ui/toaster';
import { Toaster as Sonner } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import Index from './pages/Index';
import { ErrorPage } from './pages/ErrorPage';
import { Redirect } from './pages/Redirect';
import { Inspector, InspectorInstance } from './pages/Inspector';
import { useEffect, useRef, useState } from 'react';
import { cssStringToObject, PostMessageType, ReviewMessageType } from '@/lib/utils';

const queryClient = new QueryClient();

const App = () => {
  // IMPORTANT: DO NOT REMOVE THIS JSX OR THIS COMMENT!  START 👇
  const inspectorRef = useRef<InspectorInstance>(null);

  const [key, setKey] = useState(0);

  const [isEditing, setIsEditing] = useState(false);

  const onMessage = (event: MessageEvent) => {
    try {
      const data = typeof event.data !== 'string' ? event.data : JSON.parse(event.data);

      if (!data.type) return;

      if (data.type === ReviewMessageType.stoppedEditing) {
        inspectorRef.current?.disabled();
        setTimeout(() => {
          setIsEditing(false);
          setKey(prev => prev + 1);
          window.parent.postMessage({ type: PostMessageType.stoppedEditing }, '*');
        }, 0);
      }

      if (data.type === ReviewMessageType.startedEditing) {
        setIsEditing(true);
        setKey(prev => prev + 1);
        window.parent.postMessage({ type: PostMessageType.startedEditing }, '*');
      }

      if (data.type === ReviewMessageType.hideOverlay) {
        inspectorRef.current?.hideOverlay();
      }

      if (data.type === ReviewMessageType.deleteElement) {
        if (!data.payload?.id) return;
        const { id } = data.payload;
        const element = document.querySelector(`[data-aippy-id="${id}"]`);

        if (element) element.remove();
      }

      if (data.type === ReviewMessageType.updateStyle) {
        if (!data.payload?.id || !data.payload?.style) return;
        const { id, style } = data.payload;
        const elements = document.querySelectorAll(`[data-aippy-id="${id}"]`);
        const styleObject = typeof style === 'string' ? cssStringToObject(style) : style;
        elements.forEach(el => {
          if (el instanceof HTMLElement) {
            Object.assign(el.style, styleObject);
          }
        });
      }

      // updateContent
      if (data.type === ReviewMessageType.updateContent) {
        if (!data.payload?.id || !data.payload?.content) return;
        const { id, content } = data.payload;
        const element = document.querySelector(`[data-aippy-id="${id}"]`);

        if (element) element.innerHTML = content;
      }

      // updateSrc when element is img
      if (data.type === ReviewMessageType.updateSrc) {
        if (!data.payload?.id || !data.payload?.src) return;
        const { id, src } = data.payload;
        const element = document.querySelector(`[data-aippy-id="${id}"]`);
        if (element) (element as HTMLImageElement).src = src;
      }

      if (data.type === ReviewMessageType.updateHref) {
        if (!data.payload?.id || !data.payload?.href) return;
        const { id, href } = data.payload;
        const element = document.querySelector(`[data-aippy-id="${id}"]`);
        if (element) (element as HTMLAnchorElement).href = href;
      }

      return;
    } catch (error) {
      console.error('Error parsing message', error);
    }
  };

  useEffect(() => {
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  // IMPORTANT: DO NOT REMOVE THIS JSX OR THIS COMMENT!  END 👆
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        {/* IMPORTANT: DO NOT REMOVE THIS DOM TAG OR THIS COMMENT!  START 👇 */}
        {isEditing && <Inspector key={key} ref={inspectorRef} />}
        {/* IMPORTANT: DO NOT REMOVE THIS DOM TAG OR THIS COMMENT!  END 👆 */}
        <BrowserRouter>
          <Routes>
            <Route path="/" errorElement={<ErrorPage />} element={<Index />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE AND ADD ERROR PAGE example: <Route path="*" errorElement={<ErrorPage />} element={<NotFound />} /> */}
            <Route path="*" errorElement={<ErrorPage />} element={<Redirect />} />
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
